import * as React from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, Clock, Loader2, Mail, Repeat } from "lucide-react";
import { z } from "zod";
import { sendPriceUpdateEmailsPriceUpdateSendEmailPost, type GetPriceUpdateDataByDateResponse } from "@/client-admin";
import { Button, buttonVariants } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { DatePicker } from "@/components/DatePicker";
import { ComparisonDialog } from "@/components/PriceUpdate/ComparisonDialog";
import { ContractNameValidationDialog } from "@/components/PriceUpdate/ContractNameValidationDialog";
import { PriceUpdateTable } from "@/components/PriceUpdate/PriceUpdateTable";
import { cn } from "@/lib/utils";
import { getPreviousWorkDay } from "@/routes/_superuser/admin/price-update/review-upload";
import { ResetSentStatusDialog } from "./ResetSentStatusDialog";

const emailFormSchema = z.object({
  message: z.string().optional().nullable().default(null),
});

type EmailFormValues = z.infer<typeof emailFormSchema>;

interface ReviewPriceUploadDataProps {
  getPriceUpdateResponse: GetPriceUpdateDataByDateResponse | null;
}

export function ReviewPriceUploadData({ getPriceUpdateResponse }: ReviewPriceUploadDataProps) {
  const { toast } = useToast();
  const router = useRouter();
  const [selectedDate, setSelectedDate] = React.useState<Date>(() => {
    return getPreviousWorkDay();
  });

  const form = useForm<z.input<typeof emailFormSchema>, unknown, EmailFormValues>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      message: "",
    },
  });

  const { mutateAsync: sendPriceUpdateEmail, isPending: isSendingPriceUpdateEmail } = useMutation({
    mutationFn: ({ message }: { message?: string | null }) =>
      sendPriceUpdateEmailsPriceUpdateSendEmailPost({ message }),
    onSuccess: (response) => {
      router.invalidate();
      toast({
        title: "Success",
        description: response.message,
        duration: Infinity,
      });
      form.reset();
    },
    onError: (error) => {
      toast({
        title: "Error sending price update email",
        description: error.message,
      });
    },
  });

  const routeData = getPriceUpdateResponse;

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  if (!routeData?.data) {
    return <div>No data</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Status Badges */}
      <div className="flex flex-wrap gap-4">
        <div
          className={cn(
            "flex items-center gap-2 rounded-full px-4 py-2",
            routeData.sent_at ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700",
          )}
        >
          {routeData.sent_at ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
          <span className="font-medium">
            {routeData.sent_at ? `Email sent at ${new Date(routeData.sent_at).toLocaleString()}` : "Email pending"}
          </span>
        </div>

        <div className="flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-blue-700">
          <Clock className="h-5 w-5" />
          <span className="font-medium">Created: {new Date(routeData.created_at).toLocaleString()}</span>
        </div>

        {routeData.updated_at && (
          <div className="flex items-center gap-2 rounded-full bg-purple-100 px-4 py-2 text-purple-700">
            <Clock className="h-5 w-5" />
            <span className="font-medium">Updated: {new Date(routeData.updated_at).toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="flex flex-col gap-4 rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex gap-4">
            <DatePicker
              defaultValue={selectedDate}
              value={selectedDate}
              onChange={handleDateChange}
              className="w-[200px]"
            />
            <ComparisonDialog />
            <ContractNameValidationDialog />
          </div>

          {routeData.sent_at ? (
            <ResetSentStatusDialog />
          ) : (
            <Link className={buttonVariants({ variant: "destructive" })} to="/admin/price-update/upload">
              <Repeat className="mr-2 h-4 w-4" />
              Reupload Price Update File
            </Link>
          )}
        </div>

        {!routeData.sent_at && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(async (data) => await sendPriceUpdateEmail(data))} className="flex gap-4">
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        className="w-full"
                        placeholder="Enter email message for price update notification..."
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={isSendingPriceUpdateEmail}>
                <Mail className="mr-2 h-4 w-4" />
                {isSendingPriceUpdateEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send email"}
              </Button>
            </form>
          </Form>
        )}
      </div>

      <PriceUpdateTable data={routeData.data} />
    </div>
  );
}
